import { useState, useEffect } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const Products = () => {
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    stock_quantity: '',
    is_active: true
  })

  // Fetch products and categories
  useEffect(() => {
    fetchProducts()
    fetchCategories()
  }, [])

  const fetchProducts = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE_URL}/api/products?limit=100`)
      if (response.ok) {
        const data = await response.json()
        setProducts(data)
      } else {
        setError('Failed to fetch products')
      }
    } catch (error) {
      setError('Error fetching products: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`)
      if (response.ok) {
        const data = await response.json()
        setCategories(data)
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: '',
      image_url: '',
      stock_quantity: '',
      is_active: true
    })
    setShowAddForm(false)
    setEditingProduct(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      const token = localStorage.getItem('adminToken')
      if (!token) {
        alert('Please login as admin first')
        return
      }

      console.log('Admin Token:', token) // Debug log

      const method = editingProduct ? 'PUT' : 'POST'
      const url = editingProduct 
        ? `${API_BASE_URL}/api/products/${editingProduct.id}`
        : `${API_BASE_URL}/api/products`
      
      console.log('Making request to:', url, 'with method:', method) // Debug log
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      })

      console.log('Response status:', response.status) // Debug log
      
      if (response.ok) {
        await fetchProducts() // Refresh the products list
        resetForm()
        alert(editingProduct ? 'Product updated successfully!' : 'Product added successfully!')
      } else {
        const errorData = await response.json()
        console.error('Error response:', errorData) // Debug log
        alert('Error: ' + (errorData.message || 'Failed to save product'))
      }
    } catch (error) {
      console.error('Request error:', error) // Debug log
      alert('Error saving product: ' + error.message)
    }
  }

  const handleDelete = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) {
      return
    }

    try {
      const token = localStorage.getItem('adminToken')
      if (!token) {
        alert('Please login as admin first')
        return
      }

      console.log('Delete request - Admin Token:', token) // Debug log
      console.log('Deleting product ID:', productId) // Debug log

      const response = await fetch(`${API_BASE_URL}/api/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      console.log('Delete response status:', response.status) // Debug log

      if (response.ok) {
        await fetchProducts() // Refresh the products list
        alert('Product deleted successfully!')
      } else {
        const errorData = await response.json()
        console.error('Delete error response:', errorData) // Debug log
        alert('Error: ' + (errorData.message || 'Failed to delete product'))
      }
    } catch (error) {
      console.error('Delete request error:', error) // Debug log
      alert('Error deleting product: ' + error.message)
    }
  }

  const handleEdit = (product) => {
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      category_id: product.category_id,
      image_url: product.image_url,
      stock_quantity: product.stock_quantity,
      is_active: product.is_active
    })
    setEditingProduct(product)
    setShowAddForm(true)
  }

  // Filter products based on search and category
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === '' || product.category_id.toString() === selectedCategory
    return matchesSearch && matchesCategory
  })

  return (
    <div className="fade-in">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem' 
      }}>
        <h1 style={{ color: 'var(--text-primary)', margin: 0 }}>
          📦 Product Management
        </h1>
        <button 
          className="primary-btn"
          onClick={() => setShowAddForm(true)}
          style={{
            background: 'linear-gradient(135deg, #00ff88, #00d4ff)',
            color: '#000',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '12px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ➕ Add Product
        </button>
      </div>

      {/* Search and Filter */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            minWidth: '200px',
            padding: '0.75rem',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-card)',
            color: 'var(--text-primary)'
          }}
        />
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{
            padding: '0.75rem',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            minWidth: '150px'
          }}
        >
          <option value="">All Categories</option>
          {categories.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Add/Edit Product Form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '1.5rem' }}>
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1rem'
          }}>
            <input
              type="text"
              name="name"
              placeholder="Product Name"
              value={formData.name}
              onChange={handleInputChange}
              style={{
                padding: '0.75rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)'
              }}
            />
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleInputChange}
              style={{
                padding: '0.75rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)'
              }}
            >
              <option value="">Select Category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <input
              type="number"
              name="price"
              placeholder="Price"
              value={formData.price}
              onChange={handleInputChange}
              style={{
                padding: '0.75rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)'
              }}
            />
            <input
              type="number"
              name="stock_quantity"
              placeholder="Stock Quantity"
              value={formData.stock_quantity}
              onChange={handleInputChange}
              style={{
                padding: '0.75rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)'
              }}
            />
            <input
              type="url"
              name="image_url"
              placeholder="Image URL"
              value={formData.image_url}
              onChange={handleInputChange}
              style={{
                padding: '0.75rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                gridColumn: 'span 2'
              }}
            />
          </div>
          <textarea
            name="description"
            placeholder="Product Description"
            value={formData.description}
            onChange={handleInputChange}
            rows="3"
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '12px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              marginTop: '1rem',
              resize: 'vertical'
            }}
          />
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginTop: '1.5rem',
            alignItems: 'center'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--text-primary)'
            }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              Active Product
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '1rem' }}>
              <button
                onClick={resetForm}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #00ff88, #00d4ff)',
                  color: '#000',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {editingProduct ? 'Update Product' : 'Add Product'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Products List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          ⏳ Loading products...
        </div>
      ) : error ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem', 
          color: '#ff006e',
          background: 'var(--bg-card)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 0, 110, 0.3)'
        }}>
          ❌ {error}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredProducts.map(product => (
            <div
              key={product.id}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: '16px',
                padding: '1.5rem',
                transition: 'transform 0.3s ease'
              }}
            >
              {product.image_url && (
                <img
                  src={product.image_url}
                  alt={product.name}
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '12px',
                    marginBottom: '1rem'
                  }}
                />
              )}
              <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0' }}>
                {product.name}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                {product.description}
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ color: '#00ff88', fontWeight: '600', fontSize: '1.2rem' }}>
                  ₹{product.price}
                </span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                  Stock: {product.stock_quantity}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => handleEdit(product)}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    borderRadius: '8px',
                    border: '1px solid #00d4ff',
                    background: 'transparent',
                    color: '#00d4ff',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    borderRadius: '8px',
                    border: '1px solid #ff006e',
                    background: 'transparent',
                    color: '#ff006e',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredProducts.length === 0 && !loading && !error && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: 'var(--text-secondary)',
          background: 'var(--bg-card)',
          borderRadius: '16px',
          border: '1px solid var(--border-color)'
        }}>
          📦 No products found
        </div>
      )}
    </div>
  )
}

export default Products
